import type {
  GameItemExtend,
  CasinoGameItemExtend,
  RecentGameResponse,
} from '~/types/game'
import {
  AUTH_API,
  CURRENT_PAGED,
  LOBBY_GAME_LIMIT,
  LOBBY_GAME_MOBILE_LIMIT,
} from '~/constants'
import { useApiFetch } from '@kira-dancer/nuxt-common/composables/service/use-api-fetch'

export function useCustomRecentGame() {
  const { isMobile } = useDevice()
  const { request } = useApiFetch()
  const isLoading = ref(false)
  const allRecentGameName = reactive<string[]>([])
  const allRecentGameMobileDisplay = reactive<GameItemExtend[]>([])
  const currentPerPage = ref(CURRENT_PAGED)
  const { isLogged } = useUser()
  const { allCasinoGames, allGames, fetchAllCasinoGames, fetchAllGames } =
    useFetchGames()

  const limitPerPage = computed(() => {
    return isMobile ? LOBBY_GAME_MOBILE_LIMIT : LOBBY_GAME_LIMIT
  })

  const getGameName = (item: GameItemExtend | CasinoGameItemExtend): string => {
    return `${item.provider}_${item.gameId ?? item.tableId ?? item.tableCode}`
  }

  const fetchRecentGames = async (): Promise<void> => {
    if (!isLogged.value) {
      return
    }
    try {
      // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
      const { data } = await request<void, RecentGameResponse[]>(
        AUTH_API.RECENT_GAME,
      )
      if (data.length) {
        allRecentGameName.splice(0)
        const gameFormat =
          data.map((item) => {
            return normalizeVietnameseWords(getGameName(item))
          }) ?? []

        allRecentGameName.push(...gameFormat)
      }
    } catch {
      allRecentGameName.splice(0)
    }
  }

  const fetchAllCasinoAndGames = async (): Promise<void> => {
    isLoading.value = true
    try {
      if (!allCasinoGames?.value?.length || !allGames?.value?.length) {
        await Promise.all([fetchAllCasinoGames(), fetchAllGames()])
      }
      await fetchRecentGames()
    } catch {
      //
    } finally {
      isLoading.value = false
    }
  }

  const allLobbyAndCasinoGames = computed(() => {
    const allItems: GameItemExtend[] =
      allRecentGameName.map((item) => {
        const itemGame = allGames?.value?.find(
          (game) => item === normalizeVietnameseWords(getGameName(game)),
        ) as GameItemExtend
        if (itemGame) {
          return itemGame
        }
        const itemCasino = allCasinoGames?.value?.find(
          (game) => item === normalizeVietnameseWords(getGameName(game)),
        ) as GameItemExtend
        if (itemCasino) {
          return { ...itemCasino, isCasino: true }
        }
        return {}
      }) || []
    return allItems
  })

  onMounted(async () => {
    scrollToTop()
    if (isLogged.value) {
      await fetchAllCasinoAndGames()
      if (isMobile) {
        allRecentGameMobileDisplay.splice(0)
        allRecentGameMobileDisplay.push(
          ...allLobbyAndCasinoGames.value.slice(0, limitPerPage.value),
        )
      }
    }
  })

  const totalResults = computed(() => {
    return allLobbyAndCasinoGames.value?.length
  })

  const allRecentGameDisplay = computed(() => {
    const offset: number = (currentPerPage.value - 1) * limitPerPage.value
    const itemPerPage: number = Math.min(
      offset + limitPerPage.value,
      totalResults.value,
    )
    return allLobbyAndCasinoGames.value.slice(offset, itemPerPage)
  })

  const isEmpty = computed(() => totalResults.value === 0 && !isLoading.value)

  const isShowPagination = computed(() => {
    return totalResults.value > limitPerPage.value
  })

  const changePage = (page: number) => {
    currentPerPage.value = page
    scrollToTop()
  }

  const onLoadMore = () => {
    currentPerPage.value += limitPerPage.value
    allRecentGameMobileDisplay.push(
      ...allLobbyAndCasinoGames.value.slice(
        currentPerPage.value,
        currentPerPage.value + limitPerPage.value,
      ),
    )
  }

  return {
    isLoading,
    currentPerPage,
    totalResults,
    isEmpty,
    limitPerPage,
    isShowPagination,
    allRecentGameDisplay,
    allRecentGameMobileDisplay,
    changePage,
    onLoadMore,
  }
}
